import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import "../../styles/common/dropdownSelect.scss";

const DropdownSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  isMulti = false,
  searchable = true,
  inputId,
  disable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);

  const normalized = useMemo(() => {
    return (options || []).map((o) => ({
      ...o,
      value: o.value ?? o.id ?? String(o),
      label: o.label ?? o.name ?? String(o),
    }));
  }, [options]);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return normalized;
    const q = query.toLowerCase();
    return normalized.filter((o) => o.label.toLowerCase().includes(q));
  }, [normalized, searchable, query]);

  const isSelected = (opt) => {
    if (isMulti) {
      const arr = Array.isArray(value) ? value : [];
      return arr.some((v) => (v.value ?? v) === opt.value);
    }
    return (value?.value ?? value) === opt.value;
  };

  const currentLabel = () => {
    if (isMulti) {
      const arr = Array.isArray(value) ? value : [];
      if (arr.length === 0) return placeholder;
      return arr.map((v) => v.label ?? v);
    }
    return value?.label ?? placeholder;
  };

  const handleOptionClick = (opt) => {
    if (isMulti) {
      const arr = Array.isArray(value) ? [...value] : [];
      const idx = arr.findIndex((v) => (v.value ?? v) === opt.value);
      let next;
      if (idx >= 0) {
        next = [...arr.slice(0, idx), ...arr.slice(idx + 1)];
      } else {
        next = [...arr, opt];
      }
      onChange && onChange(next);
    } else {
      onChange && onChange(opt);
      setOpen(false);
    }
  };

  const handleControlKeyDown = (e) => {
    if (
      !open &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(0);
    }
  };

  const handleMenuKeyDown = (e) => {
    if (!open) return;
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min((i < 0 ? 0 : i) + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max((i < 0 ? 0 : i) - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) handleOptionClick(filtered[activeIndex]);
    }
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="dd-select" ref={ref}>
      <button
        type="button"
        id={inputId}
        className={`dd-control${open ? " open" : ""}`}
        onClick={() => setOpen((s) => !s)}
        onKeyDown={handleControlKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disable}
      >
        {isMulti ? (
          <span
            className={`dd-value dd-value-multi${
              !value || !value?.length ? " placeholder" : ""
            }`}
          >
            {(!value || value.length === 0) && placeholder}
            {Array.isArray(value) && value.length > 0 && (
              <span className="dd-chips">
                {currentLabel()
                  .slice(0, 3)
                  .map((lbl, idx) => (
                    <span className="dd-chip" key={`${lbl}-${idx}`}>
                      {lbl}
                    </span>
                  ))}
                {Array.isArray(value) && value.length > 3 && (
                  <span className="dd-more">+{value.length - 3}</span>
                )}
              </span>
            )}
          </span>
        ) : (
          <span className={`dd-value${!value ? " placeholder" : ""}`}>
            {currentLabel()}
          </span>
        )}
        <span className="dd-caret" aria-hidden>
          <ExpandMoreIcon fontSize="small" />
        </span>
      </button>
      {open && (
        <div
          className="dd-menu"
          role="listbox"
          aria-labelledby={inputId}
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
        >
          {searchable && (
            <div className="dd-search">
              <span className="dd-search-icon">
                <SearchIcon fontSize="small" />
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label="Search options"
              />
            </div>
          )}
          <div className="dd-options">
            {filtered.length === 0 ? (
              <div className="dd-empty">No options</div>
            ) : (
              filtered.map((opt, idx) => {
                const checked = isSelected(opt);
                const active = idx === activeIndex;
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={checked}
                    className={`dd-option${checked ? " selected" : ""}${
                      active ? " active" : ""
                    }`}
                    onClick={() => handleOptionClick(opt)}
                  >
                    <span className="dd-label">{opt.label}</span>
                    {isMulti && (
                      <span
                        className={`dd-check${checked ? " on" : ""}`}
                        aria-hidden
                      >
                        {checked ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownSelect;
